<template>
  <v-container fluid :class="{ 'color-new-light-grey px-0' : this.$vuetify.breakpoint.mdAndUp, 'pa-0' : this.$vuetify.breakpoint.smAndDown }">
    <template v-if="!singleDebtMode">
      <template v-if="$vuetify.breakpoint.smAndDown">
        <shared-mobile-breadcrumb :title="isJointPaymentPlan ? 'headers.paymentPlan' : 'headers.singleDebt'"></shared-mobile-breadcrumb>
      </template>
      <template v-else>
        <v-row no-gutters justify="center">
          <v-breadcrumbs data-matomo-mask>
            <v-icon slot="divider">fas fa-chevron-right</v-icon>
            <v-breadcrumbs-item to="/front-page">{{ $t('menu.frontPage') }}</v-breadcrumbs-item>
            <v-breadcrumbs-item :disabled="true" v-if="isJointPaymentPlan">{{ $t('paymentPlan') }} ({{ debt.paymentPlan.nr }})</v-breadcrumbs-item>
            <v-breadcrumbs-item :disabled="true" v-else>{{ $t('debt') }} {{ debt.orderNr }} ({{ debt.creditor.name }})</v-breadcrumbs-item>
          </v-breadcrumbs>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" class="text-center justify-center align-center">
            <h2 class="mt-0 text-h2" v-if="isJointPaymentPlan">{{ $t('headers.paymentPlan') }}</h2>
            <h2 class="mt-0 text-h2" v-else>{{ $t('headers.singleDebt') }}</h2>
          </v-col>
        </v-row>
      </template>
    </template>
    <v-container :class="{ 'pa-0 fluid' : this.$vuetify.breakpoint.smAndDown }">
      <v-card light :outlined="this.$vuetify.breakpoint.mdAndUp" :flat="this.$vuetify.breakpoint.smAndDown">
        <v-row no-gutters>
          <v-col cols="12" md="4" :class="{ 'border-right summary-desktop' : this.$vuetify.breakpoint.mdAndUp }">
            <v-container class="pa-12" :class="{ 'py-3': $vuetify.breakpoint.smAndDown, 'py-6': $vuetify.breakpoint.mdAndUp }">
              <debt-summary-upper :debt="debt"></debt-summary-upper>
              <debt-summary-lower :debt="debt" v-if="this.$vuetify.breakpoint.mdAndUp"></debt-summary-lower>
              <v-row v-if="$vuetify.breakpoint.smAndDown">
                <v-col cols="12">
                  <template v-if="!debt.debtSummary.state.ended">
                    <v-btn dark large block tile color="primary" :disabled="hidePaymentButtons" class="payment-button payment-button-debt mb-4" @click="setPaymentData(debt.debtSummary.left, false)" v-if="!isJointPaymentPlan">
                      <span v-if="hasActivePaymentPlan">{{ $t('payWholeDebt') }}</span>
                      <span v-else>{{ $t('continueToPay') }}</span>
                    </v-btn>
                    <v-btn dark large block tile color="primary" v-if="allowPaymentPlanCreation && !hasPaymentPlanSuggestion" @click="paymentPlanDialog = true" class="mb-4">
                      {{ $t('createPaymentPlan') }}
                    </v-btn>
                    <v-btn dark large block tile color="color-new-dark-grey" v-if="hasPaymentPlanSuggestion" @click="showPaymentPlanSuggestion" class="mb-4">
                      <v-icon class="mr-3">fas fa-exclamation-circle</v-icon>
                      {{ $t('paymentPlan') }}
                    </v-btn>
                    <v-btn dark large block tile color="secondary" v-if="hasActivePaymentPlan" :disabled="hidePaymentButtons" @click="setPaymentData(nextInstallment.eur - nextInstallment.paid, true)" class="payment-button payment-button-debt mb-4">
                      {{ $t('d-text-19') }}
                    </v-btn>
                  </template>
                  <v-menu offset-y bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn tile
                             outlined
                             block
                             large
                             color="color-new-link-blue"
                             v-bind="attrs"
                             v-on="on">
                        {{ $t('d-text-24') }}
                      </v-btn>
                    </template>
                    <v-list v-if="!debt.debtSummary.state.ended || !disableReports">
                      <template v-if="!debt.debtSummary.state.ended">
                        <v-list-item class="transfer-dd-button" @click.stop="transferDueDateDialog = true">
                          {{ $t('transferDueDate') }}
                        </v-list-item>
                        <v-list-item @click.stop="customPaymentDialog = true">
                          {{ $t('d-text-10') }}
                        </v-list-item>
                        <v-list-item v-if="debt.isInvoiceCopyRequestAllowed" @click.stop="startInvoiceCopyRequest">
                          {{ $t('d-text-22') }}
                        </v-list-item>
                        <v-list-item v-if="debt.isReclamationAllowed && (isCompany || !isMessengerEnabled)" @click.stop="startReclamationRequest">
                          {{ $t('d-text-23') }}
                        </v-list-item>
                      </template>
                      <v-list-item>
                        <shared-balance-report :debtNr="debt.nr"></shared-balance-report>
                      </v-list-item>
                      <template v-if="!disableReports">
                        <v-list-item v-if="hasActivePaymentPlan">
                          <shared-reconciliation-report :link=false :msnr="debt.paymentPlan.nr" v-if="debt.paymentPlan.paymentPlanType === 7"></shared-reconciliation-report>
                          <shared-payment-plan-report :msnr="debt.paymentPlan.nr" v-else></shared-payment-plan-report>
                        </v-list-item>
                        <v-list-item v-if="debt.isVerdictAvailable">
                          <verdict-dialog :debtNr="debt.nr" :modal="false"></verdict-dialog>
                        </v-list-item>
                      </template>
                    </v-list>
                  </v-menu>
                  <v-row v-if="debt.debtSummary.state.ended">
                    <v-row no-gutters v-if="debt.debtSummary.state.state === 8">
                      <p class="mt-3 font-weight-bold">{{ $t('paidDebtHeader') }}</p>
                      <v-col cols="12">
                        <p class="mt-3">{{ $t('paidDebtText') }}</p>
                        <p class="mt-3">{{ $t('paidDebtAdditionalText') }}</p>
                      </v-col>
                    </v-row>
                    <v-row no-gutters v-if="debt.debtSummary.state.state === 9">
                      <v-col cols="12">
                        <p class="mt-3 font-weight-bold">{{ $t('endedDebtHeader') }}</p>
                        <p class="mt-3">{{ $t('endedDebtText') }}</p>
                      </v-col>
                    </v-row>
                    <v-row no-gutters v-if="debt.debtSummary.state.state === 10">
                      <v-col cols="12">
                        <p class="mt-3 font-weight-bold">{{ $t('groundlessDebtHeader') }}</p>
                        <p class="mt-3">{{ $t('groundlessDebtText') }}</p>
                      </v-col>
                    </v-row>
                    <v-row no-gutters v-if="debt.showDebtRestructuringWarning">
                      <v-col cols="12">
                        <p class="mt-3">{{ $t('debtRestructuringNotion-1') }}</p>
                        <p class="mt-3">{{ $t('debtRestructuringNotion-2') }}</p>
                      </v-col>
                    </v-row>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
            <template v-if="$vuetify.breakpoint.smAndDown">
              <v-divider></v-divider>
              <v-container class="px-12 py-6">
                <v-row>
                  <v-col cols="12">
                    <p class="text-center ma-0"
                       :class="{'ok-title small' : $vuetify.breakpoint.smAndDown, 'text-h3' : $vuetify.breakpoint.mdAndUp }"
                       v-if="!debt.debtSummary.state.ended">
                      {{ $t('d-text-25') }}
                    </p>
                  </v-col>
                  <v-col cols="12">
                    <a @click="mobileDebtInfoDialog = true" class="text-uppercase font-weight-bold">
                      <v-icon color="color-new-link-blue" left small>fas fa-chevron-right</v-icon>
                      {{ $t('debtInformation') }}
                    </a>
                  </v-col>
                  <v-col cols="12">
                    <a @click="mobileBillDialog = true" class="text-uppercase font-weight-bold">
                      <v-icon color="color-new-link-blue" left small>fas fa-chevron-right</v-icon>
                      {{ $t('debtDetails') }}
                    </a>
                  </v-col>
                  <v-col cols="12">
                    <a @click="mobilePaymentsDialog = true" class="text-uppercase font-weight-bold">
                      <v-icon color="color-new-link-blue" left small>fas fa-chevron-right</v-icon>
                      {{ $t('payments') }}
                    </a>
                  </v-col>
                  <v-col cols="12" v-if="hasActivePaymentPlan">
                    <a @click="mobilePaymentPlanDialog = true" class="text-uppercase font-weight-bold">
                      <v-icon color="color-new-link-blue" left small>fas fa-chevron-right</v-icon>
                      {{ $t('paymentPlan') }}
                    </a>
                  </v-col>
                  <v-col cols="12">
                    <a @click="mobileActionsDialog = true" class="text-uppercase font-weight-bold">
                      <v-icon color="color-new-link-blue" left small>fas fa-chevron-right</v-icon>
                      {{ $t('actions') }}
                    </a>
                  </v-col>
                  <v-col cols="12" v-if="isUpiOrder">
                    <a @click="mobileUpiPrivacyPolicyDialog = true" class="text-uppercase font-weight-bold">
                      <v-icon color="color-new-link-blue" left small>fas fa-chevron-right</v-icon>
                      {{ $t('privacyPolicy') }}
                    </a>
                  </v-col>
                </v-row>
              </v-container>
              <v-divider></v-divider>
              <v-container class="px-12 py-6">
                <v-row>
                  <v-col cols="12" class="text-center">
                    <v-icon style="font-size: 85px;" color="color-new-link-blue">fas fa-question-circle</v-icon>
                  </v-col>
                  <v-col cols="12">
                    <h3 class="mt-0 text-h3">{{ $t('d-text-26') }}</h3>
                    <p class="mb-0">{{ $t('d-text-27') }}</p>
                  </v-col>
                  <v-col cols="12" class="text-center">
                    <v-btn tile outlined color="color-new-link-blue" to="/help">{{ $t('d-text-28') }}</v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </v-col>
          <v-col cols="12" md="8" v-if="$vuetify.breakpoint.mdAndUp">
            <v-tabs v-model="tab" show-arrows background-color="transparent" slider-color="white" color="color-new-body-grey" class="ok-tabs tab-single-debt color-new-tab-grey pa-4 pb-0" @change="tabPageEvent">
              <v-tab class="text-subtitle-1 px-8 mr-6" :ripple="false" href="#tab-case-paid" v-if="debt.debtSummary.state.ended">
                {{ $t('d-text-4') }}
              </v-tab>
              <v-tab class="text-subtitle-1 px-8 mr-6" :ripple="false" href="#tab-pay-or-paymentplan" v-if="!debt.debtSummary.state.ended">
                <span v-if="!hasActivePaymentPlan && showPaymentPlan">{{ $t('d-text-5') }}</span>
                <span v-else>{{ $t('d-text-6') }}</span>
              </v-tab>
              <v-tab class="text-subtitle-1 px-8 mr-6" :ripple="false" href="#tab-debt-information" v-if="!isJointPaymentPlan">
                {{ $t('d-text-7') }}
              </v-tab>
              <v-tab class="text-subtitle-1 px-8 mr-6" :ripple="false" href="#tab-paymentplan-information" v-if="isJointPaymentPlan">
                {{ $t('d-text-18') }}
              </v-tab>
              <v-menu offset-y bottom class="v-tabs__div" v-if="!debt.debtSummary.state.ended">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn dark
                         text
                         class="v-tab text-subtitle-1 color-new-body-grey--text font-weight-bold px-8"
                         v-bind="attrs"
                         height="auto"
                         v-on="on">
                    {{ $t('d-text-8') }}
                    <v-icon small class="ml-2">fas fa-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item class="transfer-dd-button" @click.stop="transferDueDateDialog = true">
                    {{ $t('transferDueDate') }}
                  </v-list-item>
                  <v-list-item @click.stop="customPaymentDialog = true">
                    {{ $t('d-text-10') }}
                  </v-list-item>
                  <v-list-item v-if="debt.isInvoiceCopyRequestAllowed" @click.stop="startInvoiceCopyRequest">
                    {{ $t('d-text-22') }}
                  </v-list-item>
                  <v-list-item v-if="debt.isReclamationAllowed && (isCompany || !isMessengerEnabled)" @click.stop="startReclamationRequest">
                    {{ $t('d-text-23') }}
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-tabs>
            <v-tabs-items v-model="tab" touchless>
              <v-tab-item value="tab-case-paid" v-if="debt.debtSummary.state.ended">
                <v-row no-gutters justify="center" v-if="debt.debtSummary.state.state === 8">
                  <h2 class="text-h2">{{ $t('paidDebtHeader') }}</h2>
                  <v-col cols="12">
                    <p class="text-h6 text-center font-weight-bold">{{ $t('paidDebtText') }}</p>
                    <p class="text-h6 text-center font-weight-bold">{{ $t('paidDebtAdditionalText') }}</p>
                  </v-col>
                </v-row>
                <v-row no-gutters justify="center" v-if="debt.debtSummary.state.state === 9">
                  <h2 class="text-h2">{{ $t('endedDebtHeader') }}</h2>
                  <v-col cols="12">
                    <p class="text-h6 text-center font-weight-bold">{{ $t('endedDebtText') }}</p>
                  </v-col>
                </v-row>
                <v-row no-gutters justify="center" v-if="debt.debtSummary.state.state === 10">
                  <h2 class="text-h2">{{ $t('groundlessDebtHeader') }}</h2>
                  <v-col cols="12">
                    <p class="text-h6 text-center font-weight-bold">{{ $t('groundlessDebtText') }}</p>
                  </v-col>
                </v-row>
                <v-row no-gutters v-if="debt.showDebtRestructuringWarning">
                  <v-col cols="12" class="pa-6">
                    <p class="text-center">
                      {{ $t('debtRestructuringNotion-1') }}
                    </p>
                    <p class="text-center">
                      {{ $t('debtRestructuringNotion-2') }}
                    </p>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item value="tab-pay-or-paymentplan" v-if="!debt.debtSummary.state.ended">
                <fade-transition mode="out-in">
                  <v-row no-gutters key="create-pp" v-if="!hasActivePaymentPlan && showPaymentPlan && allowPaymentPlanCreation">
                    <v-col cols="12" class="px-16 py-12">
                      <shared-create-payment-plan :debt="debt"></shared-create-payment-plan>
                    </v-col>
                    <v-col cols="12" class="text-center py-4" style="border-top: 1px solid lightgrey;">
                      <span class="mr-4">{{ $t('d-text-14') }}</span>
                      <v-btn tile outlined dark color="color-new-link-blue" @click="showPaymentPlanClick(false)">{{ $t('payWholeDebt') }}</v-btn>
                    </v-col>
                  </v-row>
                  <v-row no-gutters key="show-pp" v-if="hasActivePaymentPlan && showPaymentPlan">
                    <v-col cols="12" :class="{ 'px-16 py-12' : this.$vuetify.breakpoint.mdAndUp, 'pa-3': this.$vuetify.breakpoint.smAndDown }">
                      <v-row>
                        <v-col cols="12">
                          <h3 class="mt-0 text-h3">{{ $t('d-text-16') }}</h3>
                          <p v-if="debt.showDebtRestructuringWarning">
                            {{ $t('debtRestructuringNotion-1') }}
                          </p>
                          <p v-if="debt.showDebtRestructuringWarning">
                            {{ $t('debtRestructuringNotion-2') }}
                          </p>
                          <p v-if="isJointPaymentPlan">{{ $t('thisIsMultidebt') }}</p>
                          <p>{{ $t('installmentSequence') }} {{ nextInstallment.sequence }} / {{ debt.paymentPlan.installments.length }}</p>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <p class="ok-title big">{{ $t('amountToPay') }}</p>
                          <p class="ok-subtitle big">{{ nextInstallment.eur - nextInstallment.paid | currency }}</p>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <p class="ok-title big">{{ $t('dueDate') }}</p>
                          <p class="ok-subtitle big">{{ nextInstallment.duedate | formatDate }}</p>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <span>{{ $t('d-text-31') }}</span>
                        </v-col>
                        <v-col cols="12" sm="6" class="text-center text-sm-left">
                          <v-btn large color="primary" :disabled="hidePaymentButtons" tile @click="setPaymentData(nextInstallment.eur - nextInstallment.paid, true)" class="payment-button payment-button-installment">{{  $t('continueToPay') }}</v-btn>
                        </v-col>
                        <v-col cols="12" sm="6" class="text-center text-sm-left">
                          <v-btn large tile outlined dark color="color-new-link-blue" class="font-weight-bold transfer-dd-button" @click="transferDueDateDialog = true">{{  $t('transferDueDate') }}</v-btn>
                        </v-col>
                      </v-row>
                      <v-row v-if="debt.debtSummary.left > 0">
                        <v-col cols="12">
                          <v-expansion-panels flat>
                            <v-expansion-panel class="payment-info-panel">
                              <v-expansion-panel-header style="display: inline-block;" class="text-center" expand-icon="fas fa-caret-down">
                                <span class="ok-title font-weight-bold mb-0 mr-4" style="vertical-align: text-top;">{{ $t('d-text-32') }}</span>
                              </v-expansion-panel-header>
                              <v-expansion-panel-content class="pa-0">
                                <v-row>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{ $t('accountIban') }}</p>
                                    <p class="ok-subtitle small">{{ bankAccount.iban }}</p>
                                  </v-col>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{ $t('bankBic') }}</p>
                                    <p class="ok-subtitle small">{{ bankAccount.bic }}</p>
                                  </v-col>
                                </v-row>
                                <v-row>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{  $t('referenceNr') }}</p>
                                    <p class="ok-subtitle small" data-matomo-mask>{{ debt.debtSummary.reference }}</p>
                                  </v-col>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{  $t('paymentReceiver') }}</p>
                                    <p class="ok-subtitle small">B2 Impact</p>
                                  </v-col>
                                </v-row>
                                <v-row>
                                  <v-col cols="12" class="mt-3">
                                    <p>{{ $t('d-text-33') }}</p>
                                    <p>{{ $t('d-text-34') }}</p>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12" class="text-center py-4" style="border-top: 1px solid lightgrey;" v-if="!isJointPaymentPlan">
                      <span class="mr-4">{{ $t('d-text-14') }}</span>
                      <v-btn tile outlined dark color="color-new-link-blue" @click="showPaymentPlanClick(false)">{{ $t('payWholeDebt') }}</v-btn>
                    </v-col>
                  </v-row>
                  <v-row no-gutters key="show-debt" v-if="!showPaymentPlan">
                    <v-col cols="12" :class="{ 'pa-16' : this.$vuetify.breakpoint.mdAndUp, 'pa-3': this.$vuetify.breakpoint.smAndDown }">
                      <v-row no-gutters class="pb-2">
                        <v-col cols="12">
                          <h3 class="mt-0 text-h3">{{ $t('payWithSinglePayment') }}</h3>
                          <p v-if="debt.debtSummary.state.state === 5" v-html="$t('distraintPaymentWarning')" />
                          <p v-if="debt.showDebtRestructuringWarning">
                            {{ $t('debtRestructuringNotion-1') }}
                          </p>
                          <p v-if="debt.showDebtRestructuringWarning">
                            {{ $t('debtRestructuringNotion-2') }}
                          </p>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <p class="ok-title big">{{ $t('amountToPay') }}</p>
                          <p class="ok-subtitle big">{{ debt.debtSummary.left | currency }}</p>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <p class="ok-title big">{{ $t('dueDate') }}</p>
                          <p class="ok-subtitle big">{{ debt.dueDate | formatDate }}</p>
                        </v-col>
                      </v-row>
                      <v-row v-if="debt.debtSummary.left > 0">
                        <v-col cols="12">
                          <span>{{ $t('d-text-31') }}</span>
                        </v-col>
                        <v-col cols="12" sm="6" class="text-center text-sm-left">
                          <v-btn large color="primary" tile :disabled="hidePaymentButtons" @click="setPaymentData(debt.debtSummary.left, false)" class="payment-button payment-button-debt">{{ $t('continueToPay') }}</v-btn>
                        </v-col>
                        <v-col cols="12" sm="6" class="text-center text-sm-left">
                          <v-btn large tile outlined dark color="color-new-link-blue" class="font-weight-bold transfer-dd-button" @click="transferDueDateDialog = true">{{ $t('transferDueDate') }}</v-btn>
                        </v-col>
                      </v-row>
                      <v-row v-if="debt.debtSummary.left > 0">
                        <v-col cols="12">
                          <v-expansion-panels flat>
                            <v-expansion-panel class="payment-info-panel">
                              <v-expansion-panel-header style="display: inline-block;" class="text-center" expand-icon="fas fa-caret-down">
                                <span class="ok-title font-weight-bold mb-0 mr-4" style="vertical-align: text-top;">{{ $t('d-text-32') }}</span>
                              </v-expansion-panel-header>
                              <v-expansion-panel-content class="pa-0">
                                <v-row>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{ $t('accountIban') }}</p>
                                    <p class="ok-subtitle small">{{ bankAccount.iban }}</p>
                                  </v-col>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{ $t('bankBic') }}</p>
                                    <p class="ok-subtitle small">{{ bankAccount.bic }}</p>
                                  </v-col>
                                </v-row>
                                <v-row>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{  $t('referenceNr') }}</p>
                                    <p class="ok-subtitle small" data-matomo-mask>{{ debt.debtSummary.reference }}</p>
                                  </v-col>
                                  <v-col cols="12" sm="6">
                                    <p class="ok-title">{{  $t('paymentReceiver') }}</p>
                                    <p class="ok-subtitle small">B2 Impact</p>
                                  </v-col>
                                </v-row>
                                <v-row>
                                  <v-col cols="12" class="mt-3">
                                    <p>{{ $t('d-text-33') }}</p>
                                    <p>{{ $t('d-text-34') }}</p>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12" class="text-center py-4" v-if="allowPaymentPlanCreation && !hasActivePaymentPlan && !hasPaymentPlanSuggestion && !hidePaymentButtons" style="border-top: 1px solid lightgrey;">
                      <span class="mr-4">{{ $t('d-text-17') }}</span>
                      <v-btn tile outlined dark color="color-new-link-blue" @click="showPaymentPlanClick(true)">{{ $t('createPaymentPlan') }}</v-btn>
                    </v-col>
                    <v-col cols="12" class="text-center py-4" v-if="!hasActivePaymentPlan && hasPaymentPlanSuggestion && !hidePaymentButtons" style="border-top: 1px solid lightgrey;">
                      <v-icon color="color-new-dark-grey" class="mr-3">fas fa-exclamation-circle</v-icon>
                      <span class="mr-4">{{ $t('d-text-29') }}</span>
                      <v-btn tile dark color="color-new-dark-grey" @click="showPaymentPlanSuggestion">{{ $t('d-text-30') }}</v-btn>
                    </v-col>
                    <v-col cols="12" class="text-center py-4" v-if="hasActivePaymentPlan && !hidePaymentButtons" style="border-top: 1px solid lightgrey;">
                      <v-btn tile dark outlined color="color-new-link-blue" @click="showPaymentPlanClick(true)">{{ $t('payNextInstallment') }}</v-btn>
                    </v-col>
                  </v-row>
                </fade-transition>
                <shared-payment-forms :paymentData="paymentData" @cancelled="cancelled"></shared-payment-forms>
              </v-tab-item>
              <v-tab-item value="tab-debt-information" v-if="!isJointPaymentPlan">
                <v-row no-gutters>
                  <v-col cols="12" class="px-16 py-12">
                    <debt-info :debt="debt"></debt-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item value="tab-paymentplan-information" v-if="isJointPaymentPlan">
                <v-card-text>
                  <payment-plan-info :paymentPlan="debt.paymentPlan"></payment-plan-info>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
    <v-dialog v-model="transferDueDateDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth">
      <shared-duedate-transfer-dialog :debt="debt" @close="transferDueDateDialog = false"></shared-duedate-transfer-dialog>
    </v-dialog>
    <v-dialog v-model="customPaymentDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <shared-custom-payment-dialog :debt="debt" @close="customPaymentDialog = false"></shared-custom-payment-dialog>
    </v-dialog>
    <v-dialog v-if="$vuetify.breakpoint.smAndDown"
              v-model="mobilePaymentDialog"
              fullscreen
              hide-overlay
              transition="dialog-bottom-transition"
              content-class="payment-dialog"
              scrollable>
      <shared-payment-forms :paymentData="paymentData" @cancelled="cancelled"></shared-payment-forms>
    </v-dialog>
    <v-dialog v-model="paymentPlanDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <v-card class="dialog-card">
        <v-toolbar flat light color="white" v-if="dialogSettings.fullscreen">
          <v-toolbar-title>{{ $t('d-text-12') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="paymentPlanDialog = false">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <v-container fluid>
          <v-row justify="center">
            <v-col cols="11">
              <shared-create-payment-plan :debt="debt" @close="paymentPlanDialog = false"></shared-create-payment-plan>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog v-model="requestInvoiceCopyDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth">
      <shared-invoice-copy-request-dialog :debt="debt" @close="requestInvoiceCopyDialog = false"></shared-invoice-copy-request-dialog>
    </v-dialog>
    <v-dialog v-model="reclamationDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth">
      <shared-reclamation-dialog :debt="debt" @close="reclamationDialog = false"></shared-reclamation-dialog>
    </v-dialog>
    <v-dialog v-model="mobileDebtInfoDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <debt-information-dialog :debt="debt" @close="mobileDebtInfoDialog = false"></debt-information-dialog>
    </v-dialog>
    <v-dialog v-model="mobileBillDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <bill-dialog :debt="debt" @close="mobileBillDialog = false"></bill-dialog>
    </v-dialog>
    <v-dialog v-model="mobilePaymentsDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <payments-dialog :debt="debt" @close="mobilePaymentsDialog = false"></payments-dialog>
    </v-dialog>
    <v-dialog v-model="mobilePaymentPlanDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <payment-plan-dialog :paymentPlan="debt.paymentPlan" @close="mobilePaymentPlanDialog = false"></payment-plan-dialog>
    </v-dialog>
    <v-dialog v-model="mobileActionsDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <expiration-actions-dialog :debt="debt" @close="mobileActionsDialog = false"></expiration-actions-dialog>
    </v-dialog>
    <v-dialog v-model="mobileUpiPrivacyPolicyDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <upi-privacy-policy-dialog @close="mobileUpiPrivacyPolicyDialog = false"></upi-privacy-policy-dialog>
    </v-dialog>
    <v-dialog v-model="paymentPlanSuggestionDialog"
              :fullscreen="dialogSettings.fullscreen"
              :scrollable="dialogSettings.scrollable"
              :hide-overlay="dialogSettings.hideoverlay"
              :transition="dialogSettings.transition"
              :width="dialogSettings.width"
              :max-width="dialogSettings.maxwidth"
              content-class="full-screen-dialog">
      <shared-paymentplan-suggestion :paymentPlan="debt.unconfirmedPaymentPlan" @close="paymentPlanSuggestionDialog = false" @showPP="showPaymentPlanClick(true)"></shared-paymentplan-suggestion>
    </v-dialog>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import DebtInfo from './Tabs/DebtInfo.vue'

  import PaymentPlanInfo from './Tabs/PaymentPlanInfo.vue'

  import DebtSummaryUpper from './Summary/Upper.vue'
  import DebtSummaryLower from './Summary/Lower.vue'
  import DebtInformationDialog from './Dialogs/DebtInformationDialog.vue'
  import BillDialog from './Dialogs/BillDialog.vue'
  import PaymentsDialog from './Dialogs/PaymentsDialog.vue'
  import PaymentPlanDialog from './Dialogs/PaymentPlanDialog.vue'
  import VerdictDialog from '../Verdict/Verdict.vue'
  import ExpirationActionsDialog from './Dialogs/ExpirationActionsDialog.vue'
  import UpiPrivacyPolicyDialog from '../UpiPrivacyPolicy/UpiPrivacyPolicy.vue'

  export default {
    data: function () {
      return {
        paymentData: {},
        customPaymentDialog: false,
        transferDueDateDialog: false,
        showPaymentPlan: false,
        hidePaymentButtons: false,
        mobilePaymentDialog: false,
        paymentPlanDialog: false,
        mobileDebtInfoDialog: false,
        mobileBillDialog: false,
        mobilePaymentsDialog: false,
        mobilePaymentPlanDialog: false,
        tab: null,
        requestInvoiceCopyDialog: false,
        reclamationDialog: false,
        mobileActionsDialog: false,
        mobileUpiPrivacyPolicyDialog: false,
        paymentPlanSuggestionDialog: false
      }
    },
    props: ['orderNr'],
    mounted() {
      if (this.hasActivePaymentPlan) {
        this.showPaymentPlan = true
      }
    },
    computed: {
      ...mapGetters({
        bankAccount: 'bankAccount',
        singleDebtMode: 'hasOnlyOneDebt',
        isCompany: 'isCompany'
      }),
      debt() {
        return this.$store.getters.singleDebt(parseInt(this.orderNr))
      },
      hasActivePaymentPlan() {
        return this.debt.paymentPlan !== null
      },
      hasPaymentPlanSuggestion () {
        return this.debt.unconfirmedPaymentPlan !== undefined && this.debt.unconfirmedPaymentPlan !== null
      },
      allowPaymentPlanCreation() {
        return this.debt.paymentPlanParameters.allow
      },
      isJointPaymentPlan() {
        return this.hasActivePaymentPlan && this.debt.paymentPlan.isJointPaymentPlan
      },
      nextInstallment() {
        return this.debt.paymentPlan.nextInstallment
      },
      isMessengerEnabled () {
        return process.env.VUE_APP_MESSENGER_ENABLED === 'true'
      },
      disableReports () {
        return this.debt.hidePersonalInfo
      },
      isUpiOrder() {
        return this.debt.upiOrder
      },
      dialogSettings() {
        return {
          fullscreen: this.$vuetify.breakpoint.smAndDown,
          scrollable: false,
          hideoverlay: this.$vuetify.breakpoint.smAndDown,
          transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
          width: this.$vuetify.breakpoint.smAndDown ? 'auto' : '1024px',
          maxwidth: this.$vuetify.breakpoint.smAndDown ? 'none' : '90%'
        }
      }
    },
    methods: {
      setPaymentData(amount, isInstallment) {
        if (this.$vuetify.breakpoint.smAndDown) {
          this.mobilePaymentDialog = true
        } else {
          this.mobilePaymentDialog = false
        }
        this.paymentData = {
          debtNr: this.debt.orderNr,
          description: this.debt.creditor.name,
          amount: amount,
          msnr: null,
          paymentType: 'single'
        }
        this.hidePaymentButtons = true

        if (isInstallment) {
          this.$matomo && this.$matomo.trackEvent("Payment", "Payment", "Payment Installment Start")
          this.paymentData.paymentType = 'installment'
        } else {
          this.$matomo && this.$matomo.trackEvent("Payment", "Payment", "Payment Single Start")
        }
      },
      cancelled() {
        this.hidePaymentButtons = false
        this.mobilePaymentDialog = false
      },
      showPaymentPlanClick(val) {
        this.showPaymentPlan = val
        window.dispatchEvent(new Event('resize'))
      },
      startInvoiceCopyRequest () {
        this.requestInvoiceCopyDialog = true
        this.$matomo && this.$matomo.trackEvent("Action", "Request Invoice", "Invoice Start")
      },
      startReclamationRequest () {
        this.reclamationDialog = true
        this.$matomo && this.$matomo.trackEvent("Action", "Make Reclamation", "Reclamation Start")
      },
      showPaymentPlanSuggestion () {
        this.paymentPlanSuggestionDialog = true
      }
    },
    components: {
      'debt-info': DebtInfo,
      'payment-plan-info': PaymentPlanInfo,
      'debt-summary-upper': DebtSummaryUpper,
      'debt-summary-lower': DebtSummaryLower,
      'debt-information-dialog': DebtInformationDialog,
      'bill-dialog': BillDialog,
      'payments-dialog': PaymentsDialog,
      'payment-plan-dialog': PaymentPlanDialog,
      'verdict-dialog': VerdictDialog,
      'expiration-actions-dialog': ExpirationActionsDialog,
      'upi-privacy-policy-dialog': UpiPrivacyPolicyDialog
    }
  }
</script>

<i18n>
  {
  "fi": {
  "d-text-4": "Velan tilanne",
  "d-text-5": "Aloita lyhentäminen",
  "d-text-6": "Maksaminen",
  "d-text-7": "Velan tiedot",
  "d-text-8": "Toiminnot",
  "d-text-10": "Maksa vapaavalintainen summa",
  "d-text-12": "Maksusuunnitelman tekeminen",
  "d-text-13": "Aloita velan lyhentäminen valitsemalla sinulle sopivin erän suuruus:",
  "d-text-14": "Halutessasi voit maksaa myös velan pois kertamaksulla",
  "d-text-16": "Maksa velan seuraava erä",
  "d-text-17": "Voit myös tehdä velalle maksusuunnitelman",
  "d-text-18": "Suunnitelman tiedot",
  "d-text-19": "Maksa erä",
  "d-text-20": "Maksa osa",
  "d-text-21": "Vahvista maksusuunnitelma",
  "d-text-22": "Tilaa laskukopio",
  "d-text-23": "Tee reklamaatio",
  "d-text-24": "Muut toiminnot",
  "d-text-25": "Velan tiedot ja tapahtumat",
  "d-text-26": "Löydä vastaus kysymykseesi",
  "d-text-27": "Täältä löydät ohjeita velka-asioiden hoitoon sekä vastauksia usein kysyttyihin kysymyksiin.",
  "d-text-28": "Katso ohjeet ja vinkit",
  "d-text-29": "Olemme laatineet sinulle maksusuunnitelmaehdotuksen",
  "d-text-30": "Katso ehdotuksemme",
  "d-text-31": "Voit maksaa seuraavan erän pankkitunnuksilla suoraan Oma B2 Impact -palvelun kautta.",
  "d-text-32": "Näytä tarkemmat maksutiedot",
  "d-text-33": "Suosittelemme, että teet maksut suoraan Oma B2 Impact -palvelussa, niin saamme tiedon maksustasi saman tien. Maksuvälittäjänä toimii tällöin Paytrail Oyj, ja siksi tiliotteellesi muodostuu Paytrailin tili- ja viitetiedot. Paytrailin tietoja ei pidä kopioida eikä käyttää uudelleen velan maksamiseen verkkopankissa.",
  "d-text-34": "Jos teet maksun verkkopankissasi, sinun tulisi käyttää yllä olevia maksutietoja. Suorituksesi näkyy meillä tuolloin vasta 1–3 pankkipäivän kuluttua."
  },
  "se": {
  "d-text-4": "Ärendets status",
  "d-text-5": "Amortera",
  "d-text-6": "Betala",
  "d-text-7": "Skuldinformation",
  "d-text-8": "Funktioner",
  "d-text-10": "Betala valfri summa",
  "d-text-12": "Gör betalningsplan",
  "d-text-13": "Börja amorteringen genom att välja en lämplig summa:",
  "d-text-14": "Om du vill kan du även betala hela skulden på en gång",
  "d-text-16": "Betala följande rat",
  "d-text-17": "Du kan också göra en betalningsplan",
  "d-text-18": "Betalningsplanens information",
  "d-text-19": "Betala följande rat",
  "d-text-20": "Gör en delbetalning",
  "d-text-21": "Bekräfta betalningsplanen",
  "d-text-22": "Begär en fakturakopia",
  "d-text-23": "Reklamera",
  "d-text-24": "Andra funktioner",
  "d-text-25": "Skuldinformation och åtgärder",
  "d-text-26": "Frågor & svar",
  "d-text-27": "Här hittar du svar på de vanligaste frågorna om inkasso.",
  "d-text-28": "Se Frågor & svar",
  "d-text-29": "Vi har gjort ett förslag på betalnigsplan för dig",
  "d-text-30": "Se på vårt förslag ",
  "d-text-31": "Du kan betala nästa rat direkt via Min B2 Impact -tjänsten med hjälp av dina bankkoder.",
  "d-text-32": "Visa detaljerade betalningsuppgifter",
  "d-text-33": "Vi rekommenderar att du gör betalningar direkt i Min B2 Impact eftersom vi då får information om betalningen genast. I detta fall fungerar Paytrail Oyj som betaltjänstleverantör och därför kan du se Paytrails konto och referensinformation på ditt kontoutdrag. Denna information bör inte kopieras eller användas för nya betalningar av skulden i nätbanken.",
  "d-text-34": "Om du gör betalningen i din nätbank bör du använda betalningsinformationen ovan. Din betalning är då synlig för oss först efter 1–3 bankdagar."
  },
  "en": {
  "d-text-4": "Debt status",
  "d-text-5": "Start paying",
  "d-text-6": "Payment",
  "d-text-7": "Debt information",
  "d-text-8": "Actions",
  "d-text-10": "Pay a voluntary amount",
  "d-text-12": "Create payment plan",
  "d-text-13": "Start creating the payment plan by choosing an amount that suits you the best:",
  "d-text-14": "If you want you can also pay the entire debt at once",
  "d-text-16": "Pay the next installment",
  "d-text-17": "You can create a payment plan for this debt",
  "d-text-18": "Payment plan information",
  "d-text-19": "Pay the installment",
  "d-text-20": "Custom payment",
  "d-text-21": "Confirm the payment plan",
  "d-text-22": "Request a copy of invoice",
  "d-text-23": "File a complaint",
  "d-text-24": "Other actions",
  "d-text-25": "Debt information and actions",
  "d-text-26": "Help & tips",
  "d-text-27": "Here you can find the answers to the most common questions about collection",
  "d-text-28": "Go to help & tips",
  "d-text-29": "We have created a personal payment plan for you",
  "d-text-30": "Check our suggestion",
  "d-text-31": "You can easily pay the debt with your online banking codes.",
  "d-text-32": "Show detailed payment information",
  "d-text-33": "We recommend that you make payments directly via My B2 Impact service – this way we receive the information about your payment immediately. We use Paytrail as our payment provider, and their details will be shown in your bank statement. These details must not be used for future payments in your online bank.",
  "d-text-34": "If you decide to make payment via online bank on your own, please use the information above. It will take 1–3 banking days before we receive the information about your payment."
  }
  }
</i18n>

<style>
  .border-right {
    border-right: 1px solid lightgrey;
  }

  .summary-desktop {
    font-size: 0.85em;
  }

  .payment-info-panel .v-expansion-panel-content__wrap {
    padding: 0;
  }
</style>
